import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import {
  Loader2, Plus, Edit2, Trash2, Lock, Shield, Home, Users, Folder, Briefcase, Award, Heart, Star, Layers,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import InstitutionalHero from './ui/InstitutionalHero';

const API = process.env.REACT_APP_BACKEND_URL;

const ICONS = {
  'shield-check': Shield,
  shield: Shield,
  home: Home,
  users: Users,
  folder: Folder,
  briefcase: Briefcase,
  award: Award,
  heart: Heart,
  star: Star,
  layers: Layers,
};

const ICON_OPTIONS = Object.keys(ICONS);

const empty = {
  code: '',
  label: '',
  color: '#64748b',
  icon: 'folder',
  actif: true,
  ordre: 50,
  description: '',
};

export default function AdminSubscriptionTypes({ token }) {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchTypes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/subscription-types`, { headers });
      setTypes(res.data.types || []);
    } catch {
      toast.error("Impossible de charger les types de souscription");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTypes(); }, [fetchTypes]);

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setShowModal(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      code: t.code,
      label: t.label,
      color: t.color || '#64748b',
      icon: t.icon || 'folder',
      actif: t.actif !== false,
      ordre: t.ordre || 50,
      description: t.description || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.code || !form.label) {
      toast.error('Code et libellé requis');
      return;
    }
    if (!/^[a-z0-9_]+$/.test(form.code)) {
      toast.error('Code : minuscules, chiffres et _ uniquement');
      return;
    }
    if (!/^#[0-9a-fA-F]{6}$/.test(form.color)) {
      toast.error('Couleur : format #RRGGBB requis');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/api/admin/subscription-types/${editing.id}`, form, { headers });
        toast.success('Type mis à jour');
      } else {
        await axios.post(`${API}/api/admin/subscription-types`, form, { headers });
        toast.success('Type créé');
      }
      setShowModal(false);
      fetchTypes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Échec de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t) => {
    if (t.is_default) {
      toast.error("Type par défaut : désactivez-le plutôt (Actif = false)");
      return;
    }
    if (!window.confirm(`Supprimer définitivement « ${t.label} » ?`)) return;
    setDeletingId(t.id);
    try {
      await axios.delete(`${API}/api/admin/subscription-types/${t.id}`, { headers });
      toast.success('Type supprimé');
      fetchTypes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Échec de la suppression');
    } finally {
      setDeletingId(null);
    }
  };

  const toggleActif = async (t) => {
    try {
      await axios.put(
        `${API}/api/admin/subscription-types/${t.id}`,
        {
          code: t.code, label: t.label, color: t.color, icon: t.icon || 'folder',
          actif: !t.actif, ordre: t.ordre, description: t.description || '',
        },
        { headers }
      );
      fetchTypes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Échec');
    }
  };

  return (
    <div className="space-y-6" data-testid="subscription-types-admin">
      <InstitutionalHero
        gradient="bg-gradient-to-br from-slate-900 via-violet-900 to-indigo-900"
        badge="Référentiel produits"
        title="Types de souscription"
        subtitle="Définissez les catégories de souscription utilisées par les membres, le simulateur et la GED (RCDS, DOMIUM, FTPE, ou types personnalisés)."
        icon={Layers}
        actions={
          <Button
            onClick={openCreate}
            className="bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-semibold"
            data-testid="create-subscription-type-btn"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Nouveau type
          </Button>
        }
        kpis={[
          { label: 'Total', value: types.length },
          { label: 'Actifs', value: types.filter((t) => t.actif).length },
          { label: 'Personnalisés', value: types.filter((t) => !t.is_default).length },
        ]}
      />

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-violet-500" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {types.map((t) => {
            const Ico = ICONS[t.icon] || Folder;
            return (
              <div
                key={t.id}
                className={`relative bg-white rounded-xl border shadow-sm hover:shadow-md transition-all p-4 ${
                  t.actif ? '' : 'opacity-60'
                }`}
                data-testid={`subtype-card-${t.code}`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${t.color}22`, color: t.color }}
                  >
                    <Ico className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-bold text-slate-900 truncate">{t.label}</h3>
                      {t.is_default && (
                        <Badge variant="outline" className="text-[9px] gap-1">
                          <Lock className="h-2.5 w-2.5" />
                          Par défaut
                        </Badge>
                      )}
                    </div>
                    <p className="text-[10px] font-mono text-slate-400 mt-0.5">{t.code}</p>
                    {t.description && (
                      <p className="text-xs text-slate-600 mt-1.5 line-clamp-2">{t.description}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between mt-3 pt-3 border-t">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={t.actif}
                      onCheckedChange={() => toggleActif(t)}
                      data-testid={`toggle-actif-${t.code}`}
                    />
                    <span className="text-xs text-slate-500">{t.actif ? 'Actif' : 'Inactif'}</span>
                    <Badge variant="outline" className="text-[9px] ml-2">Ordre {t.ordre}</Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => openEdit(t)}
                      data-testid={`edit-subtype-${t.code}`}
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </Button>
                    {!t.is_default && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDelete(t)}
                        disabled={deletingId === t.id}
                        data-testid={`delete-subtype-${t.code}`}
                      >
                        {deletingId === t.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md" data-testid="subscription-type-form-modal">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifier le type' : 'Nouveau type de souscription'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Code technique <span className="text-red-500">*</span></Label>
              <Input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase() })}
                placeholder="ex : rcds_tertiaire"
                disabled={editing?.is_default}
                className="h-9 text-sm"
                data-testid="form-code"
              />
              <p className="text-[10px] text-slate-400 mt-1">Minuscules, chiffres, underscore. Non modifiable si par défaut.</p>
            </div>

            <div>
              <Label className="text-xs">Libellé <span className="text-red-500">*</span></Label>
              <Input
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="ex : RCDS Tertiaire"
                className="h-9 text-sm"
                data-testid="form-label"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Couleur</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={form.color}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                    className="h-9 w-12 p-1"
                  />
                  <Input
                    value={form.color}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                    className="h-9 text-sm font-mono"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Ordre d&apos;affichage</Label>
                <Input
                  type="number"
                  value={form.ordre}
                  onChange={(e) => setForm({ ...form, ordre: parseInt(e.target.value) || 50 })}
                  className="h-9 text-sm"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">Icône</Label>
              <div className="grid grid-cols-5 gap-1 mt-1">
                {ICON_OPTIONS.map((ic) => {
                  const Ic = ICONS[ic];
                  return (
                    <button
                      key={ic}
                      type="button"
                      onClick={() => setForm({ ...form, icon: ic })}
                      className={`h-9 rounded border-2 flex items-center justify-center transition-all ${
                        form.icon === ic ? 'border-violet-500 bg-violet-50' : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`icon-${ic}`}
                    >
                      <Ic className="h-4 w-4 text-slate-600" />
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-xs">Description (optionnel)</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                className="text-sm resize-none"
              />
            </div>

            <div className="flex items-center gap-2 pt-1">
              <Switch checked={form.actif} onCheckedChange={(v) => setForm({ ...form, actif: v })} data-testid="form-actif" />
              <Label className="text-xs">Actif (visible dans les listes)</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Annuler</Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="form-save-btn"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editing ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
